import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {APP_PATHS, TOKEN_KEY} from '../constants';
import {useLocalStorage} from 'hooks/useLocalStorage';
import {useUserContext} from 'providers/user-context';
import {IUser} from 'types/user';

export const useAuth = () => {
  const navigate = useNavigate();

  const {value: token, setItem: setToken, removeItem: removeToken} = useLocalStorage(TOKEN_KEY);
  const {setUser} = useUserContext();

  const handleLogin = useCallback(
    (token: string, user: IUser | null = null) => {
      // Save token to localstorage
      setToken(token);
      // If user is provided, set it in the user context
      if (!!user) {
        setUser(user);
      }
      // Redirect user after login
      navigate(APP_PATHS.OFFERS);
    },
    [navigate, setToken]
  );

  const handleLogout = useCallback(() => {
    setUser(null); // Clear user context
    removeToken(); // Remove token from localStorage
    navigate(APP_PATHS.BASE); // Redirect to home
  }, [navigate, setToken]);

  // Handle unauthorized access, clear user and token, and redirect to login (if needed)
  const handleUnautorized = useCallback((shouldNavigate = true) => {
      setUser(null);
      removeToken();
      if (shouldNavigate) {
          navigate(APP_PATHS.LOGIN);
      }
  }, [navigate, setToken]);

  return useMemo(
    () => ({
      token,
      login: handleLogin,
      logout: handleLogout,
      unauthorized: handleUnautorized
    }),
    [token, handleLogin, handleLogout, handleUnautorized]
  );
};
